import { useMemo, useState } from 'react';
import { Box, Stack, Chip, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Delete, Edit, LowPriority } from '@mui/icons-material';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import type { ScanEntry, ScanResolution } from '../../types/warehouse';

interface SessionTableProps {
  entries: ScanEntry[];
  onEdit: (entry: ScanEntry) => void;
  onDelete: (id: string) => void;
  onResolve: (entry: ScanEntry) => void;
}

type FilterKey = 'all' | 'resolved' | 'needs-mapping' | 'exceptions';

const RES_COLOR: Record<ScanResolution, 'success' | 'info' | 'warning'> = {
  matched: 'success',
  mapped: 'info',
  unresolved: 'warning',
  created_pending: 'warning',
};

/** The running session list with per-row edit/delete and a needs-mapping filter. */
export default function SessionTable({ entries, onEdit, onDelete, onResolve }: SessionTableProps): JSX.Element {
  const [filter, setFilter] = useState<FilterKey>('all');

  const needsMapping = entries.filter((e) => e.resolution === 'unresolved' || e.resolution === 'created_pending').length;
  const exceptions = entries.filter(
    (e) => e.notOnDocument || e.resolution === 'unresolved' || e.resolution === 'created_pending'
  ).length;

  const filtered = useMemo(() => {
    switch (filter) {
      case 'resolved':
        return entries.filter((e) => e.resolution === 'matched' || e.resolution === 'mapped');
      case 'needs-mapping':
        return entries.filter((e) => e.resolution === 'unresolved' || e.resolution === 'created_pending');
      case 'exceptions':
        return entries.filter((e) => e.notOnDocument || e.resolution === 'unresolved' || e.resolution === 'created_pending');
      default:
        return entries;
    }
  }, [entries, filter]);

  const columns: ResponsiveTableColumn<ScanEntry>[] = [
    {
      id: 'item',
      header: 'Item',
      cell: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {r.itemNo || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {r.itemDescription || ''}
          </Typography>
        </Box>
      ),
    },
    { id: 'gtin', header: 'GTIN', cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.gtin || '—'}</span> },
    { id: 'batch', header: 'Batch/Lot', cell: (r) => r.batchLot || '—', hideOnMobile: true },
    { id: 'exp', header: 'EXP', cell: (r) => r.expDateIso || r.expDate || '—', hideOnMobile: true },
    {
      id: 'serial',
      header: 'Serial',
      cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.serialNumber || '—'}</span>,
      hideOnMobile: true,
    },
    {
      id: 'line',
      header: 'Doc line',
      cell: (r) => (r.notOnDocument ? <Chip size="small" color="warning" variant="outlined" label="Not on doc" /> : r.sourceDocLineNum ?? '—'),
      hideOnMobile: true,
    },
    {
      id: 'resolution',
      header: 'Status',
      cell: (r) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small" color={RES_COLOR[r.resolution]} label={r.resolution} />
          {r.overQty && <Chip size="small" color="error" variant="outlined" label="over" />}
        </Stack>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      align: 'right',
      cell: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {(r.resolution === 'unresolved' || r.resolution === 'created_pending') && (
            <Tooltip title="Map / create">
              <IconButton size="small" color="warning" onClick={() => onResolve(r)} aria-label="Map or create">
                <LowPriority fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(r)} aria-label="Edit scan">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(r.id)} aria-label="Delete scan">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_e, v) => v && setFilter(v)}>
          <ToggleButton value="all">All ({entries.length})</ToggleButton>
          <ToggleButton value="resolved">Resolved</ToggleButton>
          <ToggleButton value="needs-mapping">
            Needs mapping{needsMapping > 0 ? ` (${needsMapping})` : ''}
          </ToggleButton>
          <ToggleButton value="exceptions">Exceptions{exceptions > 0 ? ` (${exceptions})` : ''}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <ResponsiveTable
        ariaLabel="Scan session list"
        columns={columns}
        rows={filtered}
        getRowKey={(r) => r.id}
        emptyMessage="No scans yet. Scan an item to begin."
        size="small"
      />
    </Box>
  );
}
